import _ from "lodash";
import React from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

import ACLStore from "lib/aclStore";
import classNames from "classnames";
import GenericListView from "UIComponents/GenericListView";
import I18nHOC from "UIComponents/I18nHOC";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import {
  getCurrentCurriculumProgramType,
  getUserType,
} from "modules/CommonSelectors";
import { generateFlatHierarchyWithValues } from "Utils/BenchmarkDataHelper";
import {
  ELEMENT_TYPE_DP_ATL,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_ATL,
  ELEMENT_TYPE_UBD_SKILL,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
} from "Constants/stringConstants";
import { getIsDynamicField } from "UnitPlanDetails/components/RightPane/AIContextBar/modules/Utils";

import PlannerElementDataSetModal from "./components/PlannerElementDataSetModal";
import PlannerElementDataSetModalV2 from "./components/PlannerElementDataSetModalV2";
import { getCollectiveDataMemoized } from "./modules/PlannerElementDataSetUtils";
import classes from "./PlannerElementDataSet.scss";
import {
  FILTER_TYPES,
  COURSE_STANDARDS_STATUS_TYPES,
} from "./modules/PlannerElementDataSetConstants";
import {
  getShouldShowNewTaggingModal,
  shouldShowFlatListForType,
} from "AppComponents/DatasetTaggingBody/modules/Utils";

class PlannerElementDataSet extends UIBaseComponent {
  constructor(props) {
    super(props);
    if (props.customRef) {
      props.customRef(this);
    }

    this.currentInitialFilters = {};

    this.showNewCommonComponent = getShouldShowNewTaggingModal({
      isDynamicField: this.props.isDynamicField,
    });

    this.state = {
      showModal: "",
    };
  }

  componentDidMount() {
    const { shouldOpenModalOnMount } = this.props;
    if (shouldOpenModalOnMount) {
      this.onEditClick();
    }
  }

  onEditClick = () => {
    this.setState({
      showModal: true,
    });
  };

  onCloseModal = () => {
    const { onBlurInputField, onClose } = this.props;
    this.setState({ showModal: false });

    onClose?.();
    onBlurInputField?.(); // This will reset parent's data structure (eg. CourseOverview)
  };

  onSave = () => {
    const { onClose } = this.props;
    this.setState({ showModal: false });
    onClose?.();
  };

  handleBulkUpdate = ({ selectedRows, nodes }) => {
    this.updateValue(selectedRows, {
      nodes,
    });

    this.onSave();
  };

  getData = ({ isStandardsField }) => {
    const {
      curriculumType,
      plannerElementType,
      value,
      plannerElementNodeSetData,
      unitPlanResolvedValue,
      academicSetupType,
    } = this.props;

    const {
      nodes,
      rootNodes,
      parentNodes,
      groupByType,
      value: filteredValue,
      shouldAlwaysInclude,
    } = getCollectiveDataMemoized({
      plannerElementType,
      curriculumType,
      mode: "view",
      unitPlanResolvedValue,
      plannerElementNodeSetData,
      value,
      academicSetupType,
    });

    let updatedNodes = nodes;
    let updatedRootNodes = rootNodes;
    if (groupByType === "SUBJECT" && !shouldAlwaysInclude) {
      const nodesToInsert = [];
      const newRootNodes = [];

      _.forEach(parentNodes, parentNode => {
        const filteredNodes = _.filter(
          nodes,
          ({ dataSetNodeWrapper = {}, parent }) => {
            const subjects = _.get(
              dataSetNodeWrapper,
              "properties.subjects",
              []
            );
            return !parent && _.find(subjects, { id: parentNode.id });
          }
        );
        if (!_.isEmpty(filteredNodes)) {
          nodesToInsert.push({
            ...parentNode,
            children: _.map(filteredNodes, "id"),
          });
          newRootNodes.push(parentNode.id);
        }
      });

      updatedNodes = _.concat(nodesToInsert, nodes);
      updatedRootNodes = newRootNodes;
    }

    const { showFlatList } = shouldShowFlatListForType({ plannerElementType });
    const { isDisciplinary = false } = plannerElementNodeSetData;

    if (showFlatList) {
      const { flatNodes, rootNodeIds } = generateFlatHierarchyWithValues({
        nodes: updatedNodes,
        values: filteredValue,
        includeNodeSet: isStandardsField && !isDisciplinary,
      });

      return {
        nodes: flatNodes,
        rootNodes: rootNodeIds,
      };
    }

    return { nodes: updatedNodes, rootNodes: updatedRootNodes };
  };

  getInitialFilters = () => {
    const { plannerElementNodeSetData } = this.props;
    const grades = _.get(plannerElementNodeSetData, "grades", []);
    const newInitialFilters = this.showNewCommonComponent
      ? { grades, archivalState: "ACTIVE" }
      : {
          [FILTER_TYPES.STATUS]: COURSE_STANDARDS_STATUS_TYPES.ACTIVE,
          [FILTER_TYPES.GRADES]: grades,
        };
    if (!_.isEqual(this.currentInitialFilters, newInitialFilters)) {
      this.currentInitialFilters = newInitialFilters;
    }
    return this.currentInitialFilters;
  };

  renderViewEmpty = () => {
    const { t } = this.props;
    return (
      <span className={classNames("text-body", classes.emptyTextContainer)}>
        {t("common:not_selected")}
      </span>
    );
  };

  renderEditEmpty = () => {
    return null;
  };

  renderEmptyState = () => {
    const { mode } = this.props;
    if (mode === "view") {
      return this.renderViewEmpty();
    } else {
      return this.renderEditEmpty();
    }
  };

  // To override the default styles we are rendering renderUI directly
  // Note: this will render both view and edit mode
  renderUI = () => {
    const { showModal } = this.state;
    const {
      label,
      plannerElementType,
      resolvedValue,
      plannerElementNodeSetData,
      value,
      parentNodeId,
      plannerElementNodes,
      unitPlanId,
      options,
      learningCourseId,
      academicSetupType,
      userType,
      isDynamicField,
    } = this.props;

    const isStandardsField = _.includes(
      [
        ELEMENT_TYPE_MYP_LEARNING_STANDARD,
        ELEMENT_TYPE_DP_LEARNING_STANDARD,
        ELEMENT_TYPE_UBD_LEARNING_STANDARD,
        ELEMENT_TYPE_PYP_LEARNING_STANDARD,
      ],
      plannerElementType
    );
    const showNewStandardsModal =
      isStandardsField &&
      (ACLStore.can("AdminPortal:StandardsV2") || academicSetupType);
    const { nodes, rootNodes } = this.getData({ isStandardsField });
    const showEmptyState = _.isEmpty(rootNodes);

    // Show code in view mode
    let showCode = false;
    switch (plannerElementType) {
      case ELEMENT_TYPE_MYP_ATL:
      case ELEMENT_TYPE_UBD_ATL:
      case ELEMENT_TYPE_UBD_SKILL:
      case ELEMENT_TYPE_DP_ATL:
        showCode = _.isEqual(userType, "staff");
        break;
      case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
      case ELEMENT_TYPE_DP_LEARNING_STANDARD:
      case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
      case ELEMENT_TYPE_PYP_LEARNING_STANDARD:
        showCode =
          ACLStore.can("FeatureFlag:ShowCodeInStandardViewMode") &&
          _.isEqual(userType, "staff");
        break;
    }
    const initialFilters = this.getInitialFilters();
    return (
      <>
        {showEmptyState ? (
          this.renderEmptyState()
        ) : (
          <GenericListView
            nodes={nodes}
            rootNodeIds={rootNodes}
            showCode={showCode}
          />
        )}
        {showModal ? (
          showNewStandardsModal ? (
            <PlannerElementDataSetModalV2
              onClose={this.onCloseModal}
              value={value}
              label={label}
              subjectIds={_.map(_.get(options, "parentNodes", []), "id")}
              parentNodeId={parentNodeId}
              options={options}
              learningCourseId={learningCourseId}
              plannerElementType={plannerElementType}
              initialFilters={initialFilters}
              resolvedValue={resolvedValue}
              onSave={this.handleBulkUpdate}
            />
          ) : (
            <PlannerElementDataSetModal
              onCloseModal={this.onCloseModal}
              label={label}
              plannerElementType={plannerElementType}
              resolvedValue={resolvedValue}
              plannerElementNodeSetData={plannerElementNodeSetData}
              value={value}
              handleBulkUpdate={this.handleBulkUpdate}
              preSelectedNodeId={parentNodeId}
              plannerElementNodes={plannerElementNodes}
              isStandardsField={isStandardsField}
              fieldId={_.get(resolvedValue, "id", "")}
              unitPlanId={unitPlanId}
              skipQuery={this.showNewCommonComponent}
              isDynamicField={isDynamicField}
            />
          )
        ) : null}
      </>
    );
  };
}

const mapStateToProps = (state, ownProps) => {
  const plannerElementNodeSetData =
    _.get(ownProps, "options.plannerElementNodeSetData", []) || {};
  const { fieldKey, unitPlanFields } = ownProps;

  const academicSetupType = _.get(
    state,
    "platform.currentCurriculumProgram.academicSetupType"
  );
  const curriculumType = getCurrentCurriculumProgramType(state);
  const { type: plannerElementType = "" } = plannerElementNodeSetData;
  const userType = getUserType(state);
  const isDynamicField = getIsDynamicField({
    field: fieldKey,
    isUnitDependent: true,
    allFields: unitPlanFields,
  });

  return {
    plannerElementNodeSetData,
    curriculumType,
    fieldValue: ownProps.fieldValue,
    unitPlanResolvedValue: _.get(ownProps, "resolvedValue", {}),
    plannerElementType,
    academicSetupType,
    userType,
    isDynamicField,
  };
};

export default compose(
  connect(mapStateToProps, null),
  I18nHOC({ resource: "unitPlan" })
)(PlannerElementDataSet);
